import { confirmDeleteChat } from '@common/Copilot/CopilotAgentChat/utils';
import { DotsHorizontalIcon } from '@prophecy/icons';
import { ICON_CLASS } from '@prophecy/icons/tokens';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Dropdown } from '@prophecy/ui-v3/Dropdown';
import { Text } from '@prophecy/ui-v3/Typography';
import styled from 'styled-components';

import { HOME_CHAT_TEST_IDS } from './constants';

const MENU_MIN_WIDTH = '160px';

const TriggerButton = styled(Button)<{ $iconSize: string }>`
  height: auto;
  padding: 0;

  .${ICON_CLASS} {
    width: ${({ $iconSize }) => $iconSize};
    height: ${({ $iconSize }) => $iconSize};
  }
`;

const preventDefault = (event: Event) => event.preventDefault();

/** Rename / Pin / Delete for one chat: the sidebar row and the chat header share it. */
export function ChatActionsMenu({
  name,
  pinned,
  iconSize = theme.sizes.x16,
  renaming,
  deleting,
  onRename,
  onTogglePin,
  onDelete
}: {
  name: string;
  pinned: boolean;
  iconSize?: string;
  /** This chat's own rename/delete already in flight — disables both so a second one can't fire. */
  renaming?: boolean;
  deleting?: boolean;
  onRename: () => void;
  onTogglePin: () => void;
  onDelete: () => Promise<void>;
}) {
  const busy = renaming || deleting;
  const confirmDelete = () => {
    confirmDeleteChat({ dataTestId: HOME_CHAT_TEST_IDS.deleteDialog, title: `Delete chat '${name}'?`, onDelete });
  };

  return (
    <Dropdown
      align='end'
      data-test-id={HOME_CHAT_TEST_IDS.conversationActions}
      overlayStyle={{ minWidth: MENU_MIN_WIDTH }}
      // a modal menu traps focus and would steal it back from the rename input as it closes
      modal={false}
      onCloseAutoFocus={preventDefault}
      overlay={
        <>
          <Dropdown.Item onSelect={onRename} disabled={busy} data-test-id={HOME_CHAT_TEST_IDS.renameItem}>
            <Text level='sm'>Rename</Text>
          </Dropdown.Item>
          <Dropdown.Item onSelect={onTogglePin} disabled={deleting} data-test-id={HOME_CHAT_TEST_IDS.pinItem}>
            <Text level='sm'>{pinned ? 'Unpin' : 'Pin'}</Text>
          </Dropdown.Item>
          <Dropdown.Item onSelect={confirmDelete} disabled={busy} data-test-id={HOME_CHAT_TEST_IDS.deleteItem}>
            <Text level='sm' tone={theme.colors.error600}>
              Delete
            </Text>
          </Dropdown.Item>
        </>
      }>
      <TriggerButton
        variant='plain'
        $iconSize={iconSize}
        aria-label={`Chat actions for ${name}`}
        icon={<DotsHorizontalIcon type='default' size='xs' color={theme.colors.gray500} />}
        data-test-id={HOME_CHAT_TEST_IDS.conversationMenu}
      />
    </Dropdown>
  );
}
